import { AlertCircle, Check, Package, Trash2, Upload } from "lucide-react";
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";

import ConfirmDialog from "@/components/common/ConfirmDialog";
import HotToast from "@/components/common/HotToast";
import businessInfoService from "@/services/businessInfoService";
import documentsService from "@/services/documentsService";

interface CourierApplicationCardProps {
  isSubmitting?: boolean;
  onDocumentUploaded?: () => void;
  onSubmit?: (data: any) => void;
  showDocuments?: boolean;
}

const CourierApplicationCard: React.FC<CourierApplicationCardProps> = ({
  isSubmitting = false,
  onDocumentUploaded,
  onSubmit,
  showDocuments = true,
}) => {
  const dayNames = {
    friday: "Friday",
    monday: "Monday",
    saturday: "Saturday",
    sunday: "Sunday",
    thursday: "Thursday",
    tuesday: "Tuesday",
    wednesday: "Wednesday",
  };

  // const [confirmOpen, setConfirmOpen] = useState(false); // Removed duplicate
  const [deleting, setDeleting] = useState(false);
  const [formData, setFormData] = useState({
    address: "",
    company_name: "",
    coverage_area: "",
    delivery_methods: {
      express: false,
      nextDay: false,
      sameDay: false,
      scheduled: false,
      standard: false,
    },
    description: "",
    documents: {
      businessLicense: null as File | null,
      courierLicense: null as File | null,
      insuranceCertificate: null as File | null,
      serviceAgreement: null as File | null,
      taxCertificate: null as File | null,
    },
    email: "",
    operatingHours: {
      friday: { close: "23:00", isOpen: true, open: "09:00" },
      monday: { close: "22:00", isOpen: true, open: "09:00" },
      saturday: { close: "23:00", isOpen: true, open: "08:00" },
      sunday: { close: "22:00", isOpen: true, open: "08:00" },
      thursday: { close: "22:00", isOpen: true, open: "09:00" },
      tuesday: { close: "22:00", isOpen: true, open: "09:00" },
      wednesday: { close: "22:00", isOpen: true, open: "09:00" },
    },
    package_types: {
      documents: false,
      fragile: false,
      largePackages: false,
      mediumPackages: false,
      perishable: false,
      smallPackages: false,
    },
    phone: "",
    service_type: "",
    website: "",
  });

  // Load business info on mount
  useEffect(() => {
    businessInfoService.getBusinessInfo().then((info) => {
      console.log("BusinessInfoService response:", info);
      const apiHours = (info.operating_hours || {}) as any;
      const defaultHours = {
        friday: { close: "", isOpen: false, open: "" },
        monday: { close: "", isOpen: false, open: "" },
        saturday: { close: "", isOpen: false, open: "" },
        sunday: { close: "", isOpen: false, open: "" },
        thursday: { close: "", isOpen: false, open: "" },
        tuesday: { close: "", isOpen: false, open: "" },
        wednesday: { close: "", isOpen: false, open: "" },
      };
      const mappedHours = Object.keys(defaultHours).reduce(
        (acc, day) => {
          const dayData = apiHours[day] || {};
          acc[day as keyof typeof defaultHours] = {
            close: dayData.close ?? "",
            isOpen: Boolean(dayData.isOpen),
            open: dayData.open ?? "",
          };
          return acc;
        },
        {} as typeof defaultHours,
      );
      // Support both camelCase and snake_case for delivery methods and package types
      const deliveryMethods = info.deliveryMethods ||
        info.deliveryMethods || {
          express: false,
          nextDay: false,
          sameDay: false,
          scheduled: false,
          standard: false,
        };
      const packageTypes = info.packageTypes ||
        info.packageTypes || {
          documents: false,
          fragile: false,
          largePackages: false,
          mediumPackages: false,
          perishable: false,
          smallPackages: false,
        };
      setFormData((prev) => ({
        ...prev,
        ...info,
        delivery_methods: deliveryMethods,
        operatingHours: mappedHours,
        package_types: packageTypes,
      }));
    });
  }, [showDocuments]);

  const [uploadedDocuments, setUploadedDocuments] = useState<{
    [key: string]: boolean;
  }>({});
  const [userDocuments, setUserDocuments] = useState<any[]>([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [replacingDoc, setReplacingDoc] = useState<null | string>(null);
  const [deletingDoc, setDeletingDoc] = useState<null | string>(null);
  // const [confirmOpen, setConfirmOpen] = useState(false); // Removed duplicate

  // Fetch uploaded documents on mount if in documents mode
  useEffect(() => {
    if (showDocuments) {
      setLoadingDocuments(true);
      documentsService.getUserDocuments().then((response) => {
        const uploadedState: { [key: string]: boolean } = {};
        if (response.documents && Array.isArray(response.documents)) {
          response.documents.forEach((doc) => {
            let key = doc.document_type || doc.type;
            if (key === "business_license") key = "businessLicense";
            if (key === "courier_license") key = "courierLicense";
            if (key === "insurance") key = "insuranceCertificate";
            if (key === "service_agreement") key = "serviceAgreement";
            if (key === "tax_document") key = "taxCertificate";
            uploadedState[key] = true;
          });
          setUserDocuments(response.documents);
        }
        setUploadedDocuments(uploadedState);
        setLoadingDocuments(false);
      });
    }
  }, [showDocuments]);
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCheckboxChange = (
    category: "delivery_methods" | "package_types",
    field: string,
    value: boolean,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value,
      },
    }));
  };

  const handleHoursChange = (
    day: keyof typeof formData.operatingHours,
    field: "close" | "isOpen" | "open",
    value: boolean | string,
  ) => {
    setFormData((prev) => ({
      ...prev,
      operatingHours: {
        ...prev.operatingHours,
        [day]: {
          ...prev.operatingHours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleFileUpload = async (documentType: string, file: File) => {
    if (showDocuments) {
      // Map all frontend keys to backend types
      const docTypeMap: { [key: string]: string } = {
        businessLicense: "business_license",
        courierLicense: "courier_license",
        insuranceCertificate: "insurance",
        serviceAgreement: "service_agreement",
        taxCertificate: "tax_document",
      };
      const backendType = docTypeMap[documentType] || "other";
      try {
        await documentsService.uploadDocument(backendType, file);
        setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
        toast.success("Document uploaded successfully.");
        if (typeof onDocumentUploaded === "function") onDocumentUploaded();
      } catch (e: any) {
        const backendMsg = e?.response?.data?.message;
        if (
          backendMsg &&
          backendMsg.includes("already exists") &&
          backendMsg.includes("replace_existing=true")
        ) {
          toast.error(
            "A document of this type already exists. To replace it, please use the 'Replace' option.",
          );
        } else if (e?.response?.data?.errors) {
          toast.error(
            "Validation error: " + JSON.stringify(e.response.data.errors),
          );
        } else if (backendMsg) {
          toast.error(backendMsg);
        } else {
          toast.error("Failed to upload document. Please try again.");
        }
      }
    } else {
      setFormData((prev) => ({
        ...prev,
        documents: {
          ...prev.documents,
          [documentType]: file,
        },
      }));
      setUploadedDocuments((prev) => ({ ...prev, [documentType]: true }));
    }
  };
  const handleDelete = async () => {
    setDeleting(true);
    try {
      // Simulate delete API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      toast.success("Application deleted successfully.");
      // setConfirmOpen(false); // Removed, not needed
    } catch (err) {
      toast.error("Failed to delete application.");
    } finally {
      setDeleting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    // Prepare payload for backend
    const payload = {
      ...formData,
      delivery_methods: formData.delivery_methods,
      operating_hours: (formData as any).operatingHours,
      package_types: formData.package_types,
    };
    try {
      await businessInfoService.updateBusinessInfo(payload);
      toast.success("Business info updated successfully.");
      if (onSubmit) onSubmit(formData);
    } catch (err: any) {
      const backendMsg = err?.response?.data?.message;
      toast.error(
        backendMsg || err.message || "Failed to update business info.",
      );
    }
  };

  const requiredDocuments = [
    {
      description: "Valid business registration and operating license",
      key: "businessLicense",
      label: "Business License",
      required: true,
    },
    {
      description: "Professional courier/delivery service license",
      key: "courierLicense",
      label: "Courier License",
      required: true,
    },
    {
      description: "Liability and cargo insurance coverage proof",
      key: "insuranceCertificate",
      label: "Insurance Certificate",
      required: true,
    },
    {
      description: "Tax registration and compliance certificate",
      key: "taxCertificate",
      label: "Tax Certificate",
      required: true,
    },
    {
      description: "Standard terms and conditions document",
      key: "serviceAgreement",
      label: "Service Agreement",
      required: false,
    },
  ];

  const deliveryMethodOptions = [
    {
      description: "Delivery within the same business day",
      key: "sameDay",
      label: "Same Day Delivery",
    },
    {
      description: "Delivery by next business day",
      key: "nextDay",
      label: "Next Day Delivery",
    },
    {
      description: "2-3 business days delivery",
      key: "standard",
      label: "Standard Delivery",
    },
    {
      description: "Priority rush delivery service",
      key: "express",
      label: "Express Delivery",
    },
    {
      description: "Delivery at specific times/dates",
      key: "scheduled",
      label: "Scheduled Delivery",
    },
  ];

  const packageTypeOptions = [
    {
      description: "Papers, contracts, legal documents",
      key: "documents",
      label: "Documents",
    },
    {
      description: "Items up to 5kg/11lbs",
      key: "smallPackages",
      label: "Small Packages",
    },
    {
      description: "Items up to 20kg/44lbs",
      key: "mediumPackages",
      label: "Medium Packages",
    },
    {
      description: "Items over 20kg/44lbs",
      key: "largePackages",
      label: "Large Packages",
    },
    {
      description: "Delicate items requiring special care",
      key: "fragile",
      label: "Fragile Items",
    },
    {
      description: "Temperature-sensitive items",
      key: "perishable",
      label: "Perishable Goods",
    },
  ];

  return (
    <div className="rounded-lg bg-white p-4 shadow-lg dark:bg-gray-800 sm:p-6 lg:p-8">
      <HotToast />
      <ConfirmDialog
        cancelText="Cancel"
        confirmText={deleting ? "Deleting..." : "Delete"}
        message="Are you sure you want to delete this application? This action cannot be undone."
        onCancel={() => {}}
        onConfirm={handleDelete}
        open={false}
        title="Delete Application"
      />
      <div className="mb-4 sm:mb-6">
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="rounded-lg bg-green-100 p-2.5 dark:bg-green-900/20 sm:p-3">
            <Package className="h-6 w-6 text-green-600 dark:text-green-400 sm:h-8 sm:w-8" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 sm:text-2xl">
              Courier Service Partnership
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 sm:text-base">
              Join our network as a professional courier service provider.
            </p>
          </div>
        </div>
      </div>

      <form className="space-y-6 sm:space-y-8" onSubmit={handleSubmit}>
        {!showDocuments && (
          <>
            {/* Company Information */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Company Information
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Company Name *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("company_name", e.target.value)
                    }
                    placeholder="Your Courier Service Name"
                    required
                    type="text"
                    value={formData.company_name || ""}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Service Type *
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("service_type", e.target.value)
                    }
                    required
                    value={formData.service_type || ""}
                  >
                    <option value="">Select Service Type</option>
                    <option value="Local Courier Service">
                      Local Courier Service
                    </option>
                    <option value="Regional Delivery Service">
                      Regional Delivery Service
                    </option>
                    <option value="National Shipping">National Shipping</option>
                    <option value="International Courier">
                      International Courier
                    </option>
                    <option value="Specialized Delivery">
                      Specialized Delivery
                    </option>
                  </select>
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Coverage Area *
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("coverage_area", e.target.value)
                    }
                    placeholder="Describe your service coverage areas (cities, regions, postal codes, etc.)"
                    required
                    rows={3}
                    value={formData.coverage_area || ""}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Business Address *
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("address", e.target.value)
                    }
                    placeholder="Full business address"
                    required
                    rows={3}
                    value={formData.address || ""}
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Phone Number *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) => handleInputChange("phone", e.target.value)}
                    placeholder="+1 (555) 123-4567"
                    required
                    type="tel"
                    value={formData.phone || ""}
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Email Address *
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    placeholder="courier@email.com"
                    readOnly
                    required
                    type="email"
                    value={formData.email || ""}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Website
                  </label>
                  <input
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("website", e.target.value)
                    }
                    placeholder="https://www.yourcourierservice.com"
                    type="url"
                    value={formData.website || ""}
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="mb-1.5 block text-xs font-medium text-gray-700 dark:text-gray-300 sm:mb-2 sm:text-sm">
                    Service Description
                  </label>
                  <textarea
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none dark:border-gray-600 dark:bg-gray-700 dark:text-white sm:text-base"
                    onChange={(e) =>
                      handleInputChange("description", e.target.value)
                    }
                    placeholder="Describe your courier services, experience, and what makes you unique..."
                    rows={4}
                    value={formData.description || ""}
                  />
                </div>
              </div>
            </div>

            {/* Operating Hours */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Operating Hours
              </h3>
              <div className="space-y-3">
                {Object.entries(formData.operatingHours).map(([day, hours]) => (
                  <div
                    className="flex flex-col gap-2.5 rounded-lg bg-gray-50 p-3 dark:bg-gray-700 sm:flex-row sm:items-center sm:justify-between sm:gap-3"
                    key={day}
                  >
                    <div className="flex items-center gap-3">
                      <input
                        checked={hours.isOpen}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        onChange={(e) =>
                          handleHoursChange(
                            day as keyof typeof formData.operatingHours,
                            "isOpen",
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <span className="min-w-[5rem] font-medium capitalize text-gray-900 dark:text-gray-100">
                        {dayNames[day as keyof typeof dayNames]}
                      </span>
                    </div>
                    {hours.isOpen ? (
                      <div className="flex flex-1 items-center gap-1.5 pl-7 sm:flex-none sm:gap-2 sm:pl-0">
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "open",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.open || ""}
                        />
                        <span className="flex-shrink-0 text-xs text-gray-500 sm:text-sm">
                          to
                        </span>
                        <input
                          className="min-w-0 flex-1 rounded border border-gray-300 px-1.5 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-600 dark:text-white sm:w-auto sm:flex-none sm:px-2"
                          onChange={(e) =>
                            handleHoursChange(
                              day as keyof typeof formData.operatingHours,
                              "close",
                              e.target.value,
                            )
                          }
                          type="time"
                          value={hours.close || ""}
                        />
                      </div>
                    ) : (
                      <span className="pl-7 text-gray-500 dark:text-gray-400 sm:pl-0">
                        Closed
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Delivery Methods */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Delivery Methods
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
                {deliveryMethodOptions.map((option) => (
                  <div
                    className="rounded-lg border border-gray-200 p-3 dark:border-gray-700 sm:p-4"
                    key={option.key}
                  >
                    <label className="flex cursor-pointer items-start gap-2.5 sm:gap-3">
                      <input
                        checked={
                          !!formData.delivery_methods?.[
                            option.key as keyof typeof formData.delivery_methods
                          ]
                        }
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:mt-1"
                        onChange={(e) =>
                          handleCheckboxChange(
                            "delivery_methods",
                            option.key,
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 sm:text-base">
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Package Types */}
            <div>
              <h3 className="mb-3 text-base font-semibold text-gray-900 dark:text-gray-100 sm:mb-4 sm:text-lg">
                Package Types Handled
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:gap-4 md:grid-cols-2 lg:grid-cols-3">
                {packageTypeOptions.map((option) => (
                  <div
                    className="rounded-lg border border-gray-200 p-3 dark:border-gray-700 sm:p-4"
                    key={option.key}
                  >
                    <label className="flex cursor-pointer items-start gap-2.5 sm:gap-3">
                      <input
                        checked={
                          formData.package_types[
                            option.key as keyof typeof formData.package_types
                          ]
                        }
                        className="mt-0.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500 sm:mt-1"
                        onChange={(e) =>
                          handleCheckboxChange(
                            "package_types",
                            option.key,
                            e.target.checked,
                          )
                        }
                        type="checkbox"
                      />
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 sm:text-base">
                          {option.label}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-gray-400 sm:text-sm">
                          {option.description}
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Required Documents */}
        {showDocuments && (
          <div>
            <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
              Required Documents
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {requiredDocuments.map((doc) => (
                <div
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                  key={doc.key}
                >
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="font-medium text-gray-900 dark:text-gray-100">
                      {doc.label}
                      {doc.required && (
                        <span className="ml-1 text-red-500">*</span>
                      )}
                    </h4>
                    {uploadedDocuments?.[doc.key] && (
                      <Check className="h-5 w-5 text-green-500" />
                    )}
                  </div>
                  <p className="mb-3 text-sm text-gray-600 dark:text-gray-400">
                    {doc.description}
                  </p>
                  {uploadedDocuments[doc.key] ? (
                    <>
                      {/* Document status and notes */}
                      {(() => {
                        // Find the document object for this key
                        const backendType = (() => {
                          if (doc.key === "businessLicense")
                            return "business_license";
                          if (doc.key === "courierLicense")
                            return "courier_license";
                          if (doc.key === "insuranceCertificate")
                            return "insurance";
                          if (doc.key === "serviceAgreement")
                            return "service_agreement";
                          if (doc.key === "taxCertificate")
                            return "tax_document";
                          return doc.key;
                        })();
                        const foundDoc = Array.isArray(userDocuments)
                          ? userDocuments.find(
                              (d: any) =>
                                (d.document_type || d.type) === backendType,
                            )
                          : null;
                        return null;
                      })()}
                      <div className="mt-2 flex items-center justify-center rounded-lg border border-green-400 bg-green-900/20 p-4 text-green-300 dark:bg-green-900/30">
                        <svg
                          className="mr-2 h-8 w-8 text-green-400"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M9 12l2 2l4 -4"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                          <circle cx="12" cy="12" r="10" />
                        </svg>
                        <span className="text-base font-semibold text-green-200">
                          Document uploaded successfully
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="text-center">
                      <Upload className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Click to upload or drag and drop
                      </p>
                      <input
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        className="hidden"
                        disabled={loadingDocuments}
                        id={`file-upload-${doc.key}`}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) handleFileUpload(doc.key, file);
                        }}
                        type="file"
                      />
                      <label
                        className="cursor-pointer text-blue-600 hover:underline"
                        htmlFor={`file-upload-${doc.key}`}
                      >
                        Upload
                      </label>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Submit Button (hidden in business documents tab) */}
        {!showDocuments && (
          <div className="flex justify-end">
            <button
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-white hover:bg-blue-700 disabled:opacity-50"
              disabled={isSubmitting}
              type="submit"
            >
              {isSubmitting ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </button>
          </div>
        )}
      </form>

      {/* Information Box */}
      <div className="mt-6 rounded-lg bg-green-50 p-4 dark:bg-green-900/20">
        <div className="flex items-start gap-3">
          <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-green-600 dark:text-green-400" />
          <div>
            <h4 className="font-medium text-green-900 dark:text-green-400">
              Partnership Opportunity
            </h4>
            <p className="mt-1 text-sm text-green-800 dark:text-green-300">
              Join our growing network of courier partners and access new
              customers, streamlined operations, and reliable payment
              processing. We provide the platform, you provide the excellent
              service.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourierApplicationCard;
